import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { QueryParams } from 'shared/types';
import { Renouvellement, RenouvellementListParams } from '../models';
import renouvellementService from '../services/renouvellement.service';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

interface RenouvellementState {
  renouvellements: Renouvellement[];
  total: number;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  hasMore: boolean;
  currentPage: number;
}

const initialState: RenouvellementState = {
  renouvellements: [],
  total: 0,
  loading: 'idle',
  error: null,
  hasMore: true,
  currentPage: 1,
};

export const fetchRenouvellements = createAsyncThunk(
  'renouvellement/fetchAll',
  async (params: QueryParams, { rejectWithValue }) => {
    try {
      const processedParams: RenouvellementListParams = {
        ...params,
        page: params.page ? Number(params.page) : undefined,
        limit: params.limit && params.limit !== 'unlimited' ? Number(params.limit) : undefined,
      };

      const data = await renouvellementService.getRenouvellements(processedParams);
      return data;
    } catch (error) {
      console.error('Failed to fetch renouvellements', error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage = axiosError.response?.data?.message;
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

const renouvellementSlice = createSlice({
  name: 'renouvellement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRenouvellements.pending, (state) => {
        state.loading = 'pending';
      })
      .addCase(fetchRenouvellements.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        const { rows, count } = action.payload;
        const page =
          typeof action.meta.arg.page === 'string' ? parseInt(action.meta.arg.page, 10) : action.meta.arg.page || 1;
        const limit =
          typeof action.meta.arg.limit === 'string' ? parseInt(action.meta.arg.limit, 10) : action.meta.arg.limit || 50;

        state.currentPage = page;

        if (page === 1) {
          state.renouvellements = rows;
        } else {
          // Prevent duplicates
          const existingIds = new Set(state.renouvellements.map((item) => item.id));
          const newItems = rows.filter((item) => !existingIds.has(item.id));
          state.renouvellements = [...state.renouvellements, ...newItems];
        }

        state.hasMore = rows.length === limit;
        state.total = count;
      })
      .addCase(fetchRenouvellements.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload as string;
        state.hasMore = false;
      });
  },
});

export const selectRenouvellements = (state: { renouvellement: RenouvellementState }) =>
  state.renouvellement.renouvellements;

export const selectRenouvellementsTotal = (state: { renouvellement: RenouvellementState }) =>
  state.renouvellement.total;

export const selectRenouvellementsLoading = (state: { renouvellement: RenouvellementState }) =>
  state.renouvellement.loading;

export const selectHasMoreRenouvellements = (state: { renouvellement: RenouvellementState }) =>
  state.renouvellement.hasMore;

export const selectCurrentPage = (state: { renouvellement: RenouvellementState }) => state.renouvellement.currentPage;

export default renouvellementSlice.reducer;
